import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt4, HiX } from 'react-icons/hi';

// ✅ IMPORT LOGO
import logo from '../assets/nethraloka.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Tele-Consultation', path: '/tele-consultation' },
    { name: 'Vision Test', path: '/vision-test' },
    { name: 'Follow-Up', path: '/follow-up' },
    { name: 'Find Help', path: '/find-help' },
    { name: 'Education', path: '/eye-education' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-3' : 'py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          className={`px-6 py-3 transition-all duration-500 flex justify-between items-center ${
            scrolled
              ? 'bg-black/60 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl'
              : 'bg-transparent'
          }`}
        >
          {/* 🔥 LOGO ONLY (BIGGER, NO SHAPE) */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="VisionCarePlus Logo"
              className="h-12 md:h-14 object-contain transition-transform duration-500 group-hover:scale-110"
            />

            <span className="font-extrabold text-xl tracking-tight text-white drop-shadow-md">
              VisionCare<span className="text-[var(--gold-primary)]">Plus</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-semibold transition-all duration-300 rounded-xl ${
                  location.pathname === link.path
                    ? 'text-[var(--gold-primary)] bg-[var(--gold-primary)]/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div className="w-px h-6 bg-white/10 mx-4" />

            <Link
              to="/tele-consultation"
              className="px-6 py-2.5 bg-[var(--gold-primary)] text-black rounded-xl text-sm font-black hover:bg-[var(--gold-light)] transition-all active:scale-95 shadow-lg shadow-yellow-900/20"
            >
              Get Expert Advice
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-white border border-white/10"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <HiX size={24} /> : <HiMenuAlt4 size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-full left-0 right-0 px-4 mt-2"
          >
            <div className="bg-black/90 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-2xl p-4 flex flex-col gap-2 uppercase tracking-widest text-[13px]">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 rounded-xl font-black transition-all ${
                    location.pathname === link.path
                      ? 'bg-[var(--gold-primary)] text-black shadow-lg shadow-yellow-900/40'
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <Link
                to="/tele-consultation"
                onClick={() => setIsOpen(false)}
                className="mt-2 px-4 py-4 bg-white text-black rounded-xl text-center font-black"
              >
                Get Expert Advice
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;